// SCR-STAFF-991 — تعدیل نظرات (US-CHN-014؛ AC-CHN-014.1، 014.2؛ BR-CHN-014): فقط نظرات با رضایت صریح انتشار در صف می‌آیند؛ Manager تأیید، رد یا
// پاسخ عمومی می‌دهد و متن نظر قابل ویرایش نیست؛ نظر دارای اطلاعات بالینی قابل شناسایی قابل انتشار نیست (`chn.review.moderate`).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ChnReviewStatus, type ChnReviewView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export const SCR_STAFF_991 = 'SCR-STAFF-991';

const STATUSES: readonly ChnReviewStatus[] = ['Pending', 'Published', 'Rejected'];

export function ReviewsModerationPage() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.review.moderate');
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ChnReviewStatus>('Pending');
  const [replying, setReplying] = useState<ChnReviewView | null>(null);
  const [reply, setReply] = useState('');
  const key = ['chn', session?.tenantId, 'reviews', status];
  const reviews = useQuery({ queryKey: key, queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/reviews', { params: { query: { status, limit: 100 } } })) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['chn', session?.tenantId, 'reviews'] });
  const approve = useMutation({ mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/channels/reviews/{id}:approve', { params: { path: { id } } })), onSuccess: refresh });
  const reject = useMutation({ mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/channels/reviews/{id}:reject', { params: { path: { id } } })), onSuccess: refresh });
  const sendReply = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/channels/reviews/{id}:reply', { params: { path: { id: replying?.id ?? '' } }, body: { reply: reply.trim() } })),
    onSuccess: () => {
      setReplying(null);
      setReply('');
      refresh();
    },
  });

  const columns: DataTableColumn<ChnReviewView>[] = [
    { id: 'rating', headerKey: 'chn:reviews.rating', cell: (row) => <Rating value={Number(row.rating)} readOnly size="small" /> },
    {
      id: 'text',
      headerKey: 'chn:reviews.text',
      cell: (row) => (
        <Stack spacing={0.5}>
          <Typography variant="body2">{row.text}</Typography>
          {row.clinicalFlag ? <Chip size="small" color="error" label={t('chn:reviews.clinical_flag')} data-testid="chn-review-clinical" /> : null}
          {row.publicReply ? (
            <Typography variant="caption" color="text.secondary">
              {t('chn:reviews.reply')}: {row.publicReply}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    { id: 'author', headerKey: 'chn:reviews.author', cell: (row) => row.displayName },
    { id: 'provider', headerKey: 'chn:reviews.provider', cell: (row) => row.providerName ?? '' },
    { id: 'consent', headerKey: 'chn:reviews.consent', cell: (row) => (row.publishConsent ? t('chn:reviews.consent_yes') : t('chn:reviews.consent_no')) },
    { id: 'submitted', headerKey: 'chn:reviews.submitted', cell: (row) => <JalaliDate value={row.submittedAt} /> },
    {
      id: 'actions',
      headerKey: 'chn:reviews.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {row.status === 'Pending' ? (
            <Button size="small" variant="contained" disabled={!write.canWrite || row.clinicalFlag || !row.publishConsent || approve.isPending} onClick={() => approve.mutate(row.id)} data-testid={`chn-review-approve-${row.id}`}>
              {t('chn:reviews.approve')}
            </Button>
          ) : null}
          {row.status !== 'Rejected' ? (
            <Button size="small" color="error" disabled={!write.canWrite || reject.isPending} onClick={() => reject.mutate(row.id)} data-testid={`chn-review-reject-${row.id}`}>
              {t('chn:reviews.reject')}
            </Button>
          ) : null}
          {row.status !== 'Rejected' ? (
            <Button size="small" disabled={!write.canWrite} onClick={() => { setReplying(row); setReply(row.publicReply ?? ''); }} data-testid={`chn-review-reply-${row.id}`}>
              {t('chn:reviews.reply')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_991}
      titleKey="chn:reviews.title"
      filters={
        <Stack spacing={1}>
          <TextField select size="small" label={t('chn:reviews.filter')} value={status} onChange={(event) => setStatus(event.target.value as ChnReviewStatus)} sx={{ maxWidth: 240 }} slotProps={{ htmlInput: { 'data-testid': 'chn-review-filter' } }}>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`chn:reviews.status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <Alert severity="info" variant="outlined">
            {t('chn:reviews.no_edit')}
          </Alert>
          {approve.error ? <ErrorState error={approve.error} compact /> : null}
          {reject.error ? <ErrorState error={reject.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        columns={columns}
        rows={reviews.data?.items}
        getRowId={(row) => row.id}
        isLoading={reviews.isPending}
        error={reviews.error}
        onRetry={() => void reviews.refetch()}
        emptyTitleKey="chn:reviews.empty"
        captionKey="chn:reviews.title"
      />
      <FormDialog
        open={replying !== null}
        titleKey="chn:reviews.reply"
        screenId={SCR_STAFF_991}
        onSubmit={() => sendReply.mutate()}
        onClose={() => setReplying(null)}
        isDirty={reply.trim() !== (replying?.publicReply ?? '')}
        isSubmitting={sendReply.isPending}
        submitError={sendReply.error}
      >
        <Typography variant="body2" color="text.secondary">
          {replying?.text}
        </Typography>
        <TextField label={t('chn:reviews.reply_label')} value={reply} onChange={(event) => setReply(event.target.value)} multiline minRows={3} slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'chn-review-reply-text' } }} />
      </FormDialog>
    </ListPage>
  );
}
